import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import stringHash from 'string-hash';
import { Orientation, Scheme, Size, Variant } from '../../Constants/System';
import { boxShadow, ShadowType } from '../../Constants/Shadows';
import Icon from './Icon';
import Button from '../Interactive/Button/Button';
import defaultImage1 from '../../Assets/avatars/default-image_bunny.svg';
import defaultImage2 from '../../Assets/avatars/default-image_giraffe.svg';
import defaultImage3 from '../../Assets/avatars/default-image_penguin.svg';
import defaultImage4 from '../../Assets/avatars/default-image_robot-c.svg';
import defaultImage5 from '../../Assets/avatars/default-image_cow.svg';
import defaultImage6 from '../../Assets/avatars/default-image_jellyfish.svg';
import defaultImage7 from '../../Assets/avatars/default-image_koala.svg';
import defaultImage8 from '../../Assets/avatars/default-image_fox.svg';
import defaultImage9 from '../../Assets/avatars/default-image_panda.svg';
import InlineSpinner from '../Loading/InlineSpinner';
import { mergeWithDefaults } from '../../Utility/PropUtils';
import TruncateSpan from './TruncateSpan';
import { CombineClasses } from '../../Utility/ReactUtils';
const defaultProps = {
    size: Size.large,
    orientation: Orientation.vertical,
    acceptedFileTypes: 'image/*',
};
export default function Avatar(providedProps) {
    const props = mergeWithDefaults(providedProps, defaultProps);
    const { size, orientation, name, description, showEdit, uploadHandler, className } = props;
    const [isLoading, setIsLoading] = useState(false);
    const fileInputRef = useRef(null);
    const onFileInputChanged = (e) => {
        if (e.target.files?.length && uploadHandler) {
            setIsLoading(true);
            uploadHandler(e.target.files[0])
                .catch((err) => {
                throw err;
            })
                .finally(() => {
                setIsLoading(false);
            });
        }
    };
    const onEditButtonClicked = (e) => {
        e.preventDefault();
        fileInputRef.current?.click();
    };
    const hasDescription = description ? true : false;
    return (_jsxs(AvatarWrapper, { "$orientation": orientation, "$hasDescription": hasDescription, className: CombineClasses(className, 'avatar'), children: [_jsx(Image, { src: getImageSource(props), "$isLoading": isLoading, "$size": size, "$orientation": orientation }), name && (_jsx(Name, { "$size": size, "$hasDescription": hasDescription, children: name })), description && _jsx(Description, { "$size": size, children: description }), showEdit && (_jsxs(_Fragment, { children: [_jsx(FileInput, { type: "file", ref: fileInputRef, onChange: onFileInputChanged, accept: props.acceptedFileTypes }), _jsx(EditButton, { "$size": size, "$orientation": orientation, variant: Variant.solid, scheme: Scheme.accent1, title: "Upload new avatar", onClick: onEditButtonClicked, disabled: isLoading, type: "button", children: _jsx(Icon, { name: "edit" }) })] })), isLoading && _jsx(StyledInlineSpinner, { "$size": size, "$orientation": orientation })] }));
}
const DefaultImages = [
    defaultImage1,
    defaultImage2,
    defaultImage3,
    defaultImage4,
    defaultImage5,
    defaultImage6,
    defaultImage7,
    defaultImage8,
    defaultImage9,
];
const getSizes = (size, theme) => {
    switch (size) {
        case Size.small:
            return {
                image: '32px',
                name: theme.typography.sm,
                description: theme.typography.xs,
            };
        case Size.medium:
            return {
                image: '48px',
                name: theme.typography.md,
                description: theme.typography.sm,
            };
        case Size.large:
            return {
                image: '120px',
                name: theme.typography.lg,
                description: theme.typography.md,
            };
    }
};
const getImageSourceByHashString = (str) => {
    const hash = stringHash(str);
    return DefaultImages[hash % DefaultImages.length];
};
const getImageSource = (props) => {
    const { src, userId, name } = props;
    if (src !== undefined && src) {
        return src;
    }
    else if (userId !== undefined) {
        return getImageSourceByHashString(userId);
    }
    else if (name !== undefined) {
        return getImageSourceByHashString(name);
    }
    else {
        return DefaultImages[Math.floor(Math.random() * DefaultImages.length)];
    }
};
const AvatarWrapper = styled.div `
	display: grid;
	position: relative;

	${(props) => props.$orientation == Orientation.vertical
    ? css `
					text-align: center;
					justify-items: center;
					grid-template-areas:
						'image'
						'name'
						'description';
				`
    : css `
					text-align: left;
					grid-template-areas:
						'image name'
						'image ${props.$hasDescription ? 'description' : 'name'}';
					grid-template-rows: 1fr 1fr;
					grid-template-columns: auto 1fr;
				`}
`;
const EditButton = styled(Button) `
	position: absolute;
	margin-left: ${(props) => props.$orientation === Orientation.vertical
    ? css `calc(${getSizes(props.$size, props.theme).image} / -2 - ${props.theme.spacing.sm})`
    : ''};
	display: ${(props) => (props.$size == Size.small ? 'none' : 'block')};
	transform: scale(${(props) => (props.$size == Size.large ? '1' : '0.5')});

	&:hover {
		display: block;
	}
`;
const Image = styled.img `
	aspect-ratio: 1 / 1;
	border-radius: 50%;
	object-fit: cover;
	grid-area: image;
	width: ${(props) => getSizes(props.$size, props.theme).image};
	margin: ${(props) => props.theme.spacing.sm};
	opacity: ${(props) => (props.$isLoading ? '0.3' : '1')};
	background: ${(props) => props.theme.palette.primary};
	justify-self: ${(props) => (props.$orientation == Orientation.vertical ? 'center' : 'end')};

	&:hover {
		${(props) => (props.$isLoading ? '' : boxShadow(ShadowType.Subtle))}
	}

	&:active {
		outline: ${(props) => (props.$isLoading ? 'none' : `2px solid ${props.theme.palette.brand1}`)};
	}

	&:hover ~ ${EditButton} {
		display: block;
	}
`;
const Name = styled(TruncateSpan) `
	grid-area: name;
	font-size: ${(props) => getSizes(props.$size, props.theme).name};
	font-weight: bold;
	align-self: ${(props) => (props.$hasDescription ? 'end' : 'center')};
	max-width: 100%;
`;
const Description = styled(TruncateSpan) `
	grid-area: description;
	font-size: ${(props) => getSizes(props.$size, props.theme).description};
	max-width: 100%;
`;
const FileInput = styled.input `
	display: none;
`;
const StyledInlineSpinner = styled(InlineSpinner) `
	--size: calc(${(props) => getSizes(props.$size, props.theme).image} / 2);
	grid-area: image;
	margin: ${(props) => `calc(${getSizes(props.$size, props.theme).image} / 4 + ${props.theme.spacing.sm}) 0 0 0`};
	justify-self: center;
`;
//# sourceMappingURL=Avatar.js.map